<template>
  <section v-if="feed" class="overflow-hidden pt-4 lg:pb-10 pb-8" data-jest="instagram-feed">
    <!-- overflow-hidden: the carousel lets the next slide peek over the edge, as in the product carousels -->
    <div class="container">
      <div class="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-6">
        <a
          :href="feed.profile.url"
          target="_blank"
          rel="noopener"
          class="flex items-center gap-3 no-underline text-pine-950"
        >
          <img
            v-if="feed.profile.image"
            :src="feed.profile.image"
            :alt="feed.profile.name"
            width="56"
            height="56"
            loading="lazy"
            class="size-14 rounded-full ring-2 ring-red-800 ring-offset-2"
          >
          <span class="grid leading-tight">
            <span class="font-semibold">{{ feed.profile.name }}</span>
            <span class="text-sm text-muted">@{{ feed.profile.username }}</span>
          </span>
        </a>
        <span v-if="feed.profile.followersCount" class="grid text-center leading-tight">
          <span class="font-semibold">{{ formatCount(feed.profile.followersCount) }}</span>
          <span class="text-xs text-muted">seuraajaa</span>
        </span>
        <BaseButton
          :href="feed.profile.url"
          target="_blank"
          rel="noopener"
          look="outline"
          class="text-sm"
        >
          Seuraa Instagramissa
        </BaseButton>
      </div>
      <ProductCarousel swiper-classes="overflow-visible xl:overflow-hidden">
        <SwiperSlide v-for="post in feed.posts" :key="post.id" class="h-auto">
          <!-- A link to Instagram (middle click, no JS); a plain click opens the post in the modal on our page -->
          <a
            :href="post.permalink"
            target="_blank"
            rel="noopener"
            class="block group relative overflow-hidden rounded"
            :title="post.caption"
            aria-haspopup="dialog"
            data-jest="instagram-tile"
            @click="openPost($event, post.id)"
          >
            <!-- Slide widths of ProductCarousel: 2.1–2.2 per view on phones (~160–180 px: the 480 px tile up to ~2.9x screens), 3.35 / 4.45 / 4 above -->
            <img
              :src="post.image"
              :srcset="post.imageSmall ? `${post.imageSmall} 480w, ${post.image} 640w` : undefined"
              :sizes="post.imageSmall ? '(min-width: 1440px) 320px, (min-width: 1024px) 22vw, (min-width: 768px) 30vw, 42vw' : undefined"
              :alt="post.caption || 'Instagram-julkaisu'"
              width="640"
              height="800"
              loading="lazy"
              decoding="async"
              class="w-full h-auto aspect-[4/5] object-cover transition-transform duration-300 group-hover:scale-105"
            >
            <span v-if="post.mediaType === 'VIDEO'" class="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white">
              <PlayIcon class="size-4" />
            </span>
            <span v-else-if="post.mediaType === 'CAROUSEL_ALBUM'" class="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white">
              <Square2StackIcon class="size-4" />
            </span>
          </a>
        </SwiperSlide>
      </ProductCarousel>
    </div>
    <LazyInstagramModal
      v-if="openPostId"
      :feed="feed"
      :start-post-id="openPostId"
      @close="openPostId = null"
    />
  </section>
</template>

<script setup lang="ts">
import { PlayIcon, Square2StackIcon } from '@heroicons/vue/24/solid'
import { SwiperSlide } from 'swiper/vue'
import ProductCarousel from '~/components/carousel/ProductCarousel.vue'
import type { InstagramFeed } from '~/types/instagram'

const { data: feed } = await useFetch<InstagramFeed | null>('/api/instagram', { key: 'instagram-feed', default: () => null })

const openPostId = ref<string | null>(null)

const openPost = (event: MouseEvent, postId: string) => {
  // Ctrl/Cmd/Shift click and middle click keep opening Instagram in a new tab
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return
  event.preventDefault()
  openPostId.value = postId
}

const formatCount = (count: number) => count >= 1000 ? `${(count / 1000).toLocaleString('fi-FI', { maximumFractionDigits: 1 })} t.` : String(count)
</script>
